import { css } from '@emotion/react'
import { useState, useEffect } from 'react'
import { colors, Divider, font, mq } from 'plume-ui'
import { getCompany } from '@/lib/api/company'
import {
  CompanySummary,
  ContentfulCompany,
  ContentfulInvestor,
  InvestorLink,
} from '../../../types'
import { desktopMinWidth } from '../../tokens/mediaquery'
import { getInvestorLink } from '@/lib/api/investor'
import { printError } from '@/lib/log'

type Props = {
  companies?: ContentfulCompany[]
  investors?: ContentfulInvestor[]
}

export const RelatedOrganizations: React.FC<Props> = (props) => {
  if (!props.companies && !props.investors) {
    return null
  }

  return (
    <>
      <Divider
        css={css`
          margin: 0 8px;
          ${mq.large} {
            margin: 0;
          }
        `}
      />
      <div
        css={css`
          margin: 24px;
          ${mq.large} {
            margin: 24px 48px 0;
          }
        `}
      >
        <h4
          css={css`
            color: ${colors.textStrong};
            font-size: 16px;
            ${mq.large} {
              font-size: 14px;
            }
          `}
        >
          関連する企業
        </h4>
        <div
          className="related-companies"
          css={css`
            display: flex;
            flex-flow: wrap;
          `}
        >
          {props.companies
            ? props.companies.map((company, i) => (
                <CompanyBanner company={company} key={i} />
              ))
            : null}
          {props.investors
            ? props.investors.map((investor, i) => (
                <InvestorBanner investor={investor} key={i} />
              ))
            : null}
        </div>
      </div>
      <Divider
        css={css`
          ${mq.large} {
            display: none;
          }
        `}
      />
    </>
  )
}

const CompanyBanner: React.FC<{ company: ContentfulCompany }> = (props) => {
  const [company, setCompany] = useState<CompanySummary>()

  useEffect(() => {
    getCompany(props.company.shortname)
      .then((res) => {
        setCompany(res.data)
      })
      .catch((e) => printError(e))
  }, [props.company.shortname])

  if (!company) {
    return null
  }

  return <OrganizationBanner organization={company}></OrganizationBanner>
}

const InvestorBanner: React.FC<{ investor: ContentfulInvestor }> = (props) => {
  const [investor, setInvestor] = useState<InvestorLink>()

  useEffect(() => {
    getInvestorLink(props.investor.shortname)
      .then((res) => {
        setInvestor(res.data)
      })
      .catch((e) => printError(e))
  }, [props.investor.shortname])

  if (!investor) {
    return null
  }

  return <OrganizationBanner organization={investor}></OrganizationBanner>
}

const OrganizationBanner: React.FC<{
  organization: InvestorLink | CompanySummary
}> = (props) => {
  return (
    <div
      css={css`
        display: flex;
        align-items: center;
        flex-basis: 100%;
        padding: 5px 0;
        @media (min-width: ${desktopMinWidth}) {
          flex-basis: 50%;
          padding: 6px 12px 6px 0;
        }
      `}
    >
      <a
        href={props.organization.path}
        css={css`
          border: 1px solid ${colors.borderLight};
          border-radius: 2px;
          flex-shrink: 0;
          width: 50px;
          height: 50px;
          padding: 2px;
          display: flex;
          align-items: center;
          margin-right: 8px;
          background-color: ${colors.white00};
          &:hover {
            box-shadow: 0px 0px 10px rgba(0, 0, 0, 0.1);
          }
        `}
      >
        <img
          src={props.organization.imageUrl}
          css={css`
            width: 100%;
            height: auto;
          `}
        ></img>
      </a>
      <a
        href={props.organization.path}
        css={css`
          color: ${colors.text};
          font-size: 14px;
          font-weight: ${font.weight.bold};
          line-height: 1.5;
          text-decoration: underline solid transparent;
          &:hover {
            text-decoration: underline;
          }
          ${mq.large} {
            font-size: 16px;
          }
        `}
      >
        {props.organization.name}
      </a>
    </div>
  )
}
